/* eslint-disable array-bracket-newline */
import { useState, useEffect } from 'react';
import { BigNumber } from 'bignumber.js';
import { ITicker } from '@kattana-io/common';
import { ICoin } from '../reducers/dex/tradingReducer';
import { Direction } from '.';
import { estimateSuccessCallbackType } from './useTradeEstimate';
import { getPriceUSDForPair } from '../helpers/statistics';
import {
  ITonSwapSimulateResponse,
  estimateTonFees,
  getTonTokensRates,
  simulateTonSwap,
} from '../helpers/tonTrading';
import { PROXY_TON } from '../helpers/nativeCoins';

interface TonEstimateProps {
  meta?: ITicker;
  base: ICoin;
  quote: ICoin;
  account?: string;
  direction: Direction; // 0 - fixed input, 1 - fixed output
  slippage: number; // 0.01 -> 1%
  estimateIndex: number;
  enabled: boolean;
  onEstimateStart: (base: boolean) => void;
  onEstimateError: () => void;
  onEstimateSuccess: (args: estimateSuccessCallbackType) => void;
}

type estimateType = (controller: AbortController) => Promise<void>;

const useTonEstimate = ({
  meta,
  base,
  quote,
  account,
  direction,
  slippage,
  estimateIndex,
  enabled,
  onEstimateStart,
  onEstimateError,
  onEstimateSuccess,
}: TonEstimateProps) => {
  const [price, setPrice] = useState(BigNumber(0));
  const [priceUsd, setPriceUsd] = useState({
    base: BigNumber(0),
    quote: BigNumber(0),
  });
  const [tokenRoute, setTokenRoute] = useState<ITonSwapSimulateResponse>();
  const [priceImpact, setPriceImpact] = useState<number>(0);
  const [gasFees, setGasFees] = useState<string>('0');

  const estimateBaseQuote: estimateType = async (controller) => {
    onEstimateStart(false);

    const amountIn = BigNumber(base.amount)
      .times(10 ** base.decimals)
      .toString();
    const route = await simulateTonSwap(
      base.address,
      quote.address,
      amountIn,
      slippage / 100,
      controller
    );

    if (!route) {
      if (!controller.signal.aborted) {
        return onEstimateError();
      }
      return;
    }

    setPrice(BigNumber(1).div(route.swap_rate));
    setTokenRoute(route);
    setPriceImpact(Number(route.price_impact));

    const naturalAmount = BigNumber(route.ask_units).div(
      10 ** Number(quote.decimals)
    );
    const afterSlippage = (100 - slippage) / 100;
    const minReceived = BigNumber(route.min_ask_units).div(
      10 ** quote.decimals
    );

    onEstimateSuccess({
      naturalAmount,
      afterSlippage,
      minReceived,
    });
  };

  /**
   * Effect 1
   * Base->Quote
   */
  useEffect(() => {
    const controller = new AbortController();
    if (direction == Direction.BaseQuote && enabled) {
      if (base.amount.gt(0)) {
        estimateBaseQuote(controller);
      }
    }

    return () => {
      controller.abort();
    };
  }, [base, slippage, estimateIndex, enabled, direction]);

  useEffect(() => {
    const controller = new AbortController();

    const baseAddr = base.address === PROXY_TON ? 'TON' : base.address;
    const quoteAddr = quote.address === PROXY_TON ? 'TON' : quote.address;

    getTonTokensRates([baseAddr, quoteAddr], controller).then((rates) => {
      if (!controller.signal.aborted) {
        setPriceUsd({
          base: BigNumber(
            rates?.[baseAddr]?.prices.USD ||
              getPriceUSDForPair(meta, base.address)
          ),
          quote: BigNumber(
            rates?.[quoteAddr]?.prices.USD ||
              getPriceUSDForPair(meta, quote.address)
          ),
        });
        setPrice(
          BigNumber(
            base.address === meta?.quote_address
              ? meta.price_a
              : meta?.price_b || 0
          )
        );
      }
    });

    return () => controller.abort();
  }, [base.address, quote.address, meta]);

  const estimateQuoteBase: estimateType = async (controller) => {
    onEstimateStart(true);

    const amountIn = BigNumber(quote.amount)
      .times(10 ** quote.decimals)
      .toString();
    const route = await simulateTonSwap(
      quote.address,
      base.address,
      amountIn,
      slippage / 100,
      controller
    );

    if (!route) {
      if (!controller.signal.aborted) {
        return onEstimateError();
      }
      return;
    }

    setPrice(BigNumber(route.swap_rate));
    setTokenRoute(route);
    setPriceImpact(Number(route.price_impact));

    const naturalAmount = BigNumber(route.ask_units).div(10 ** base.decimals);
    const quoteNaturalAmount = BigNumber(route.offer_units).div(
      10 ** quote.decimals
    );
    const afterSlippage = (100 - slippage) / 100;

    onEstimateSuccess({
      naturalAmount,
      afterSlippage,
      quoteNaturalAmount,
    });
  };

  /**
   * Effect 2
   * Quote->Base
   */
  useEffect(() => {
    const controller = new AbortController();
    if (direction == Direction.QuoteBase && enabled) {
      if (quote.amount.gt(0)) {
        estimateQuoteBase(controller);
      }
    }

    return () => {
      controller.abort();
    };
  }, [quote, slippage, estimateIndex, enabled, direction]);

  /**
   * Effect 3
   * Update gap price when we have provider and price route
   */
  useEffect(() => {
    let active = true;

    if (tokenRoute && account) {
      estimateTonFees(
        account,
        tokenRoute.offer_address,
        tokenRoute.ask_address,
        tokenRoute.offer_units,
        tokenRoute.min_ask_units
      )
        .then((gas) => {
          if (active) {
            setGasFees(gas);
          }
        })
        .catch((e) => console.error(e));
    }

    return () => {
      active = false;
    };
  }, [tokenRoute]);

  return { price, priceUsd, tokenRoute, gasFees, priceImpact };
};

export default useTonEstimate;
